import type { AppError } from '@aibi/shared';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PasswordInput,
  Spinner,
} from '@aibi/ui';
import { CircleAlert, KeyRound, ShieldCheck } from 'lucide-react';
import { useState, type SyntheticEvent, type ReactNode } from 'react';
import { useAdminSession } from '../state/admin-session';
import { ErrorNotice } from './ErrorNotice';
import { PASSWORD_MIN_LENGTH } from './form-rules';

/**
 * Muestra sus hijos solo con sesion de administrador. Si no hay administrador, guia la
 * creacion con el codigo de un solo uso que el servidor muestra en su consola.
 */
export function AdminGate({
  children,
  loginDescription,
}: {
  readonly children: ReactNode;
  /** Motivo del acceso que se muestra al pedir la contrasena. */
  readonly loginDescription?: string;
}) {
  const session = useAdminSession();
  const { status } = session;

  if (status.phase === 'checking') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Comprobando la sesion" />
      </div>
    );
  }
  if (status.phase === 'unreachable') {
    return (
      <EmptyState
        tone="error"
        icon={<CircleAlert />}
        title="No se pudo contactar con el servidor"
        description={`${status.error.message} La configuracion requiere el servidor en ejecucion.`}
        action={
          <Button size="sm" variant="outline" onClick={session.refresh}>
            Reintentar
          </Button>
        }
      />
    );
  }
  if (status.phase === 'setup') return <SetupForm />;
  if (status.phase === 'login') {
    return <LoginForm expired={status.expired} description={loginDescription} />;
  }
  return <>{children}</>;
}

function GateCard({
  icon,
  title,
  description,
  children,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-1 items-start justify-center overflow-auto p-6 sm:items-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-1 flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary [&_svg]:size-4">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {children}
      </Card>
    </div>
  );
}

function SetupForm() {
  const { services, signedIn } = useAdminSession();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  const errors = {
    code: code.trim() === '' ? 'Escriba el codigo que aparece en la consola del servidor.' : null,
    password:
      password.length < PASSWORD_MIN_LENGTH
        ? `Use al menos ${String(PASSWORD_MIN_LENGTH)} caracteres.`
        : null,
    confirm: confirm !== password ? 'Las contrasenas no coinciden.' : null,
  };
  const valid = !errors.code && !errors.password && !errors.confirm;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const result = await services.auth.setup(code, password);
    setBusy(false);
    if (result.ok) {
      setPassword('');
      setConfirm('');
      signedIn(result.value);
    } else {
      setError(result.error);
    }
  };

  return (
    <GateCard
      icon={<ShieldCheck />}
      title="Crear la cuenta de administrador"
      description={
        <>
          Es el primer acceso a la configuracion. Copie el codigo de un solo uso que el servidor
          muestra en su consola al arrancar y elija una contrasena.
        </>
      }
    >
      <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Codigo de configuracion" error={touched ? errors.code : null}>
          {(control) => (
            <Input
              {...control}
              value={code}
              autoComplete="off"
              spellCheck={false}
              placeholder="XXXXXX-XXXXXX"
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Contrasena" error={touched ? errors.password : null}>
          {(control) => (
            <PasswordInput
              {...control}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Repita la contrasena" error={touched ? errors.confirm : null}>
          {(control) => (
            <PasswordInput
              {...control}
              value={confirm}
              onChange={(event) => {
                setConfirm(event.target.value);
              }}
            />
          )}
        </Field>
        {error && <ErrorNotice error={error} />}
        <Button type="submit" disabled={busy}>
          {busy ? 'Creando...' : 'Crear administrador'}
        </Button>
      </form>
    </GateCard>
  );
}

function LoginForm({
  expired,
  description = 'La configuracion de conexiones y del asistente de IA requiere la contrasena de administrador.',
}: {
  readonly expired: boolean;
  readonly description?: string | undefined;
}) {
  const { services, signedIn } = useAdminSession();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const missing = password === '' ? 'Escriba la contrasena.' : null;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setTouched(true);
    if (missing || busy) return;
    setBusy(true);
    setError(null);
    const result = await services.auth.login(password);
    setBusy(false);
    setPassword('');
    if (result.ok) signedIn(result.value);
    else setError(result.error);
  };

  return (
    <GateCard
      icon={<KeyRound />}
      title="Acceso de administrador"
      description={
        expired ? 'La sesion ha caducado. Vuelva a iniciar sesion para continuar.' : description
      }
    >
      <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Contrasena" error={touched ? missing : null}>
          {(control) => (
            <PasswordInput
              {...control}
              value={password}
              autoComplete="current-password"
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          )}
        </Field>
        {error && <ErrorNotice error={error} />}
        <Button type="submit" disabled={busy}>
          {busy ? 'Entrando...' : 'Iniciar sesion'}
        </Button>
        <p className="text-[11px] text-muted-foreground">
          Si olvido la contrasena, quien instalo el servidor puede restablecerla (ver la guia de
          administracion).
        </p>
      </form>
    </GateCard>
  );
}
